/**
 * Close Button Component
 *
 * Specialized button for closing modals, dialogs, sheets, and other overlay components.
 * Uses consistent styling and includes proper accessibility attributes.
 *
 * Usage:
 *   <CloseButton onClick={onClose} />
 *
 *   <CloseButton size="icon-sm" variant="ghost" onClick={onClose} />
 */

import * as React from 'react';
import { X } from 'lucide-react';
import { IconButton, IconButtonProps } from './icon-button';
import { cn } from '@/lib/utils';

interface CloseButtonProps extends Omit<IconButtonProps, 'icon' | 'aria-label'> {
  /**
   * Size of the close button
   * @default 'icon-sm'
   */
  size?: 'icon' | 'icon-sm' | 'icon-lg';
  /**
   * Variant styling
   * @default 'ghost'
   */
  variant?: IconButtonProps['variant'];
  /**
   * Custom aria-label (default: "Close")
   */
  'aria-label'?: string;
  /**
   * Custom icon size (default: based on button size)
   */
  iconSize?: number;
}

/**
 * CloseButton - Button for closing modals/dialogs
 * Used for: Modal close buttons, dialog close buttons, sheet close buttons, popover close buttons
 */
export const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    {
      className,
      size = 'icon-sm',
      variant = 'ghost',
      'aria-label': ariaLabel = 'Close',
      iconSize,
      ...props
    },
    ref,
  ) => {
    // Determine icon size based on button size if not provided
    const computedIconSize =
      iconSize ||
      (size === 'icon-sm' ? 16 : size === 'icon-lg' ? 20 : 18);

    return (
      <IconButton
        ref={ref}
        icon={<X style={{ width: computedIconSize, height: computedIconSize }} />}
        size={size}
        variant={variant}
        aria-label={ariaLabel}
        className={cn(className)}
        {...props}
      />
    );
  },
);
CloseButton.displayName = 'CloseButton';
